<template>
    <!-- Own dialog shell with the CRM modal classes: the global Modal leaves its Escape listener behind. -->
    <div class="modal__mask brand-support-settings" @click="$emit('close')">
        <div class="modal__wrapper">
            <div
                ref="dialog"
                class="modal__container brand-support-settings__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="brand-support-settings-title"
                @click.stop
            >
                <div class="modal__top">
                    <div class="modal__header">
                        <h3 id="brand-support-settings-title" class="brand-support-settings__title">Компании в панели</h3>
                        <div v-if="settings" class="brand-support-settings__scope">{{ scopeText }}</div>
                    </div>
                    <button
                        type="button"
                        class="modal__button button button_with_icon button_type_icon button_color_transp-black"
                        aria-label="Закрыть"
                        @click="$emit('close')"
                    >
                        <svg aria-hidden="true" width="24" height="24"><use href="#icon-close"></use></svg>
                    </button>
                </div>

                <div class="modal__body">
                    <p v-if="!settings && !loadError" class="brand-support-settings__empty">Загружаем настройки…</p>

                    <div v-else-if="!settings" class="brand-support-settings__message" role="alert">
                        {{ loadError }}
                        <v-button class="button_color_transp-black" size="small" @click="loadSettings">Повторить</v-button>
                    </div>

                    <div v-else class="brand-support-settings__body">
                        <div
                            v-if="message"
                            class="brand-support-settings__message"
                            :class="{ 'is-info': message.info }"
                            role="alert"
                        >
                            {{ message.text }}
                        </div>

                        <section class="brand-support-settings__section">
                            <span class="brand-support-settings__label">Добавить компанию</span>
                            <v-custom-input
                                ref="search"
                                class="size_medium"
                                name="brandSupportCompanySearch"
                                type="search"
                                autocomplete="off"
                                placeholder="Название компании"
                                icon="#icon-search"
                                :value="search"
                                @input="handleSearch"
                            />
                            <div class="brand-support-settings__results">
                                <div
                                    v-for="item in results"
                                    :key="item.key"
                                    class="brand-support-settings__result"
                                >
                                    <span class="brand-support-settings__result-name">{{ item.name }}</span>
                                    <span v-if="isSelected(item)" class="brand-support-settings__tag">Уже в панели</span>
                                    <v-button
                                        v-else
                                        class="button_color_transp-black"
                                        size="small"
                                        :disabled="busyKey !== null"
                                        :loading="busyKey === `add:${item.key}`"
                                        @click="add(item)"
                                    >
                                        Добавить
                                    </v-button>
                                </div>
                                <p v-if="searchError" class="brand-support-settings__empty">
                                    {{ searchError }}
                                    <button type="button" class="brand-support-settings__link" @click="loadResults(true)">Повторить</button>
                                </p>
                                <p v-else-if="searching && !results.length" class="brand-support-settings__empty">Ищем…</p>
                                <p v-else-if="!results.length" class="brand-support-settings__empty">Ничего не нашлось</p>
                                <v-button
                                    v-if="hasMore && !searchError"
                                    class="button_color_transp-black brand-support-settings__more"
                                    size="small"
                                    :loading="searching"
                                    @click="loadResults(false)"
                                >
                                    Показать ещё
                                </v-button>
                            </div>
                        </section>

                        <section class="brand-support-settings__section">
                            <span class="brand-support-settings__label">В панели · порядок как в шапке</span>
                            <div v-if="settings.entries.length" class="brand-support-settings__entries">
                                <company-settings-row
                                    v-for="(entry, index) in settings.entries"
                                    :key="entry.key"
                                    :entry="entry"
                                    :index="index"
                                    :total="settings.entries.length"
                                    :busy="busyKey === `entry:${entry.key}`"
                                    :disabled="busyKey !== null"
                                    @move-up="move(entry, -1)"
                                    @move-down="move(entry, 1)"
                                    @remove="remove"
                                />
                            </div>
                            <p v-else class="brand-support-settings__empty">
                                В панели пока нет компаний. Найдите компанию выше и нажмите «Добавить».
                            </p>
                        </section>
                    </div>
                </div>

                <div class="modal__footer">
                    <div class="brand-support-settings__footer">
                        <small>Каждое изменение сохраняется сразу. «Убрать из панели» не удаляет компанию и её чаты</small>
                        <v-button class="button_color_black" size="small" @click="$emit('close')">Готово</v-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@api'
import vButton from '@components/global/vButton'
import vCustomInput from '@components/global/CustomInput'
import CompanySettingsRow from './CompanySettingsRow'
import { errorMessage, errorStatus, normalizeAvailable, normalizeSettings } from './contract'

const SEARCH_DEBOUNCE_MS = 300

export default {
    name: 'CompanySettingsModal',
    components: {
        vButton,
        vCustomInput,
        CompanySettingsRow,
    },
    data() {
        return {
            settings: null,
            loadError: '',
            settingsCancel: null,
            busyKey: null,
            message: null,
            search: '',
            results: [],
            page: 1,
            hasMore: false,
            searching: false,
            searchError: '',
            searchSeq: 0,
            searchCancel: null,
            searchTimer: null,
        }
    },
    computed: {
        scopeText() {
            return this.settings.scopeKind === 'personal'
                ? 'Ваш список компаний'
                : 'Настройка применяется ко всей группе поддержки'
        },
        selectedKeys() {
            if (!this.settings) return {}
            return this.settings.entries.reduce((keys, entry) => {
                if (entry.companyId !== null) keys[String(entry.companyId)] = true
                return keys
            }, {})
        },
    },
    mounted() {
        // The header is a stacking context below the side navbar; render the dialog at the body level.
        document.body.appendChild(this.$el)
        document.addEventListener('keydown', this.handleKeydown)
        // Settings are always re-read on open: a request from a closed dialog may have finished meanwhile.
        this.loadSettings()
        this.loadResults(true)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown)
        clearTimeout(this.searchTimer)
        if (this.settingsCancel) this.settingsCancel.cancel()
        if (this.searchCancel) this.searchCancel.cancel()
    },
    methods: {
        loadSettings() {
            if (this.settingsCancel) this.settingsCancel.cancel()
            const source = api.brandSupportOverview.createCancelSource()
            this.settingsCancel = source
            this.loadError = ''
            return api.brandSupportOverview.settings(source.token)
                .then(normalizeSettings)
                .then(settings => {
                    if (!settings.canManage) {
                        this.$emit('forbidden')
                        return
                    }
                    const firstLoad = !this.settings
                    this.settings = settings
                    if (firstLoad) this.$nextTick(this.focusSearch)
                })
                .catch(error => {
                    if (api.brandSupportOverview.isCancel(error)) return
                    if (errorStatus(error) === 403) {
                        this.$emit('forbidden')
                        return
                    }
                    this.loadError = errorMessage(error, 'Не удалось загрузить настройки')
                })
        },
        loadResults(reset) {
            if (this.searchCancel) this.searchCancel.cancel()
            const seq = ++this.searchSeq
            const source = api.brandSupportOverview.createCancelSource()
            const page = reset ? 1 : this.page + 1
            this.searchCancel = source
            this.searching = true
            this.searchError = ''
            api.brandSupportOverview.availableCompanies({ search: this.search.trim(), page }, source.token)
                .then(normalizeAvailable)
                .then(result => {
                    if (seq !== this.searchSeq) return
                    const known = reset ? {} : this.results.reduce((keys, item) => ({ ...keys, [item.key]: true }), {})
                    const fresh = result.items.filter(item => !known[item.key])
                    this.results = reset ? fresh : this.results.concat(fresh)
                    this.page = result.page
                    this.hasMore = result.hasMore
                })
                .catch(error => {
                    if (api.brandSupportOverview.isCancel(error) || seq !== this.searchSeq) return
                    this.searchError = errorMessage(error, 'Не удалось загрузить список компаний')
                })
                .finally(() => {
                    if (seq === this.searchSeq) this.searching = false
                })
        },
        handleSearch(value) {
            this.search = value
            clearTimeout(this.searchTimer)
            this.searchTimer = setTimeout(() => this.loadResults(true), SEARCH_DEBOUNCE_MS)
        },
        isSelected(item) {
            return item.isSelected || Boolean(this.selectedKeys[item.key])
        },

        // One mutation at a time; the list changes only after the server confirms it.
        mutate(busyKey, send) {
            if (this.busyKey !== null || !this.settings) return
            this.busyKey = busyKey
            this.message = null
            send(this.settings.version)
                .then(normalizeSettings)
                .then(settings => {
                    this.settings = settings
                    this.$emit('changed')
                })
                .catch(this.handleMutationError)
                .finally(() => {
                    this.busyKey = null
                })
        },
        handleMutationError(error) {
            const status = errorStatus(error)
            if (status === 403) {
                this.$emit('forbidden')
                return
            }
            if (status === 409) {
                this.message = { text: 'Список изменён другим пользователем. Обновляем настройки' }
                this.loadSettings().then(() => {
                    if (this.settings) {
                        this.message = { text: 'Настройки обновлены. Проверьте список и повторите действие, если оно ещё нужно', info: true }
                    }
                })
                return
            }
            if (status === 404) {
                this.message = { text: 'Эта запись уже изменилась. Список обновлён', info: true }
                this.loadSettings()
                return
            }
            this.message = { text: errorMessage(error, 'Не удалось сохранить изменение. Попробуйте ещё раз') }
        },
        add(item) {
            this.mutate(`add:${item.key}`, version => api.brandSupportOverview.addCompany(item.id, version))
        },
        remove(entry) {
            this.mutate(`entry:${entry.key}`, version => api.brandSupportOverview.removeEntry(entry.entryId, version))
        },
        move(entry, direction) {
            const entries = this.settings.entries
            const index = entries.indexOf(entry)
            const target = index + direction
            if (index < 0 || target < 0 || target >= entries.length) return
            // Up: place before the previous entry; down: before the entry after the next one (null = to the end).
            const before = direction < 0 ? entries[target] : entries[index + 2]
            const beforeId = before ? before.entryId : null
            this.mutate(`entry:${entry.key}`, version => api.brandSupportOverview.moveEntry(entry.entryId, beforeId, version))
        },

        focusSearch() {
            const search = this.$refs.search
            if (search && search.$refs.input) search.$refs.input.focus()
        },
        focusableElements() {
            const dialog = this.$refs.dialog
            if (!dialog) return []
            return Array.from(dialog.querySelectorAll('button:not([disabled]), input:not([disabled])'))
        },
        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.$emit('close')
                return
            }
            if (event.key !== 'Tab') return
            const elements = this.focusableElements()
            if (!elements.length) return
            const first = elements[0]
            const last = elements[elements.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            } else if (!this.$refs.dialog.contains(document.activeElement)) {
                event.preventDefault()
                first.focus()
            }
        },
    },
}
</script>

<style lang="scss">
@import '@scss/variables';

.brand-support-settings {
    &__dialog {
        width: 540px;
        max-width: calc(100vw - 32px);
    }

    &__title {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        line-height: 24px;
    }

    &__scope {
        margin: 4px 0 0;
        color: $global-very-gray-5b;
        font-size: 13px;
    }

    &__body {
        display: grid;
        gap: 18px;
    }

    &__section {
        display: grid;
        gap: 8px;
    }

    &__label {
        color: $global-very-gray-84;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: .05em;
        text-transform: uppercase;
    }

    &__results {
        display: grid;
        gap: 2px;
        max-height: 200px;
        overflow: auto;
        padding: 4px;
        border: 1px solid $global-light-gray;
        border-radius: 12px;
    }

    &__result {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 40px;
        padding: 4px 4px 4px 10px;
        border-radius: 8px;

        &:hover {
            background: $global-white-f6;
        }
    }

    &__result-name {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
    }

    &__tag {
        padding-right: 8px;
        color: $global-very-gray-84;
        font-size: 12px;
    }

    &__more {
        justify-self: start;
        margin: 4px;
    }

    &__entries {
        display: grid;
        gap: 6px;
    }

    &__entry {
        display: flex;
        align-items: center;
        gap: 10px;
        min-height: 50px;
        padding: 6px 6px 6px 10px;
        border: 1px solid $global-light-gray;
        border-radius: 12px;

        &.is-busy {
            opacity: .6;
        }

        &.is-gone .brand-support-settings__entry-name {
            color: $global-very-gray-84;
            font-style: italic;
        }
    }

    &__letter {
        flex: none;
        width: 30px;
        height: 30px;
        display: grid;
        place-items: center;
        border-radius: 8px;
        background: $global-white-f6;
        font-weight: 600;
    }

    &__entry-name {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;

        small {
            display: block;
            color: $global-very-gray-84;
            font-size: 12px;
            font-style: normal;
        }
    }

    &__actions {
        flex: none;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__square {
        width: 32px;
        height: 32px;
        min-width: 0;
        padding: 0;
        display: grid;
        place-items: center;
        fill: currentColor;

        &:disabled {
            opacity: .3;
            cursor: default;
        }
    }

    &__message {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 10px;
        background: $global-white-f6;
        color: $global-rage;
        font-size: 13px;

        &.is-info {
            color: $global-dark-3a;
        }
    }

    &__empty {
        margin: 0;
        padding: 12px;
        color: $global-very-gray-84;
        font-size: 13px;
        text-align: center;
    }

    &__link {
        padding: 0;
        border: 0;
        background: none;
        color: $global-purple;
        font: inherit;
        text-decoration: underline;
        cursor: pointer;
    }

    &__footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;

        small {
            max-width: 320px;
            color: $global-very-gray-84;
            font-size: 12px;
        }
    }

    button:focus-visible,
    input:focus-visible {
        outline: 2px solid $global-purple;
        outline-offset: 2px;
    }
}
</style>
